import { Avatar, Button, InlineCode, Input, Modal } from "@/atoms";
import React, { useState } from "react";
import { useMedia } from "react-use";

const DefaultRequiredTextSentence = (requiredText: string) => (
  <span className="text-sm">
    Type <InlineCode>{requiredText}</InlineCode> to confirm
  </span>
);

export type ConfirmationModalProps = {
  title: string;
  color: "brand" | "red" | "green";
  icon?: React.ReactElement<{ className?: string }>;
  confirmButtonText: string;
  cancelButtonText?: string;
  requiredText?: string;
  requiredTextSentence?: (requiredText: string) => React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  onClose?: () => void;
  onConfirm?: () => void | Promise<void>;
};

const ConfirmationModal = ({
  title,
  color,
  icon,
  confirmButtonText,
  cancelButtonText = "Cancel",
  requiredText,
  requiredTextSentence = (requiredText: string) =>
    DefaultRequiredTextSentence(requiredText),
  onConfirm,
  children,
  ...props
}: ConfirmationModalProps) => {
  const isMobile = useMedia("(max-width: 640px)");
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Modal size="lg" {...props}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setIsLoading(true);
          try {
            await onConfirm?.();
          } catch (error) {
            console.error(error);
          } finally {
            setIsLoading(false);
          }
        }}
      >
        <div className="flex flex-col items-center sm:flex-row sm:items-start">
          <Avatar
            icon={icon}
            size={isMobile ? "large" : "medium"}
            color={color}
            variant="soft"
          />
          <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Modal.Title className="text-base font-semibold leading-6 text-gray-900">
              {title}
            </Modal.Title>
            {children}
            {requiredText && (
              <div className="mt-4 flex w-full flex-col gap-2">
                {requiredTextSentence(requiredText)}
                <Input
                  className="w-full"
                  placeholder={requiredText}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-4">
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              label={cancelButtonText}
              onClick={props.onClose}
              color="gray"
              variant="outlined"
              disabled={isLoading}
            />
            <Button
              type="submit"
              label={confirmButtonText}
              color={color}
              variant="solid"
              disabled={requiredText ? inputValue !== requiredText : false}
              isLoading={isLoading}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ConfirmationModal;
